// The roast types a work order can carry. Single source of truth for the FE —
// mirrors the backend whitelist in work_order_handler.go (UpdateWorkOrderRoastType)
// and the <option> lists in BeanSelectionSection and RoastAgtronSection.
export const ROAST_TYPES = ['sample', 'filter', 'omni', 'espresso'] as const;

export type RoastType = (typeof ROAST_TYPES)[number];

const ROAST_TYPE_SET = new Set<string>(ROAST_TYPES);

/**
 * Resolves a stored roast_type string to a canonical value, or '' when the value
 * is not one this system offers.
 *
 * Both work_orders.roast_type and work_order_recipes.roast_type are plain nullable
 * TEXT with no DB constraint, so rows can hold anything a past writer put there —
 * including display-cased values ("Sample") and values that were never roast types
 * at all ("Production", "Light") written by the Artisan importer. Unknown values are
 * dropped rather than shown, so the list can never display a roast type the user
 * cannot pick or filter by.
 */
export function normalizeRoastType(value: string | null | undefined): RoastType | '' {
  const candidate = (value ?? '').trim().toLowerCase();
  return ROAST_TYPE_SET.has(candidate) ? (candidate as RoastType) : '';
}

/**
 * Picks the roast type a work order should be shown and filtered by.
 *
 * work_orders.roast_type wins: it is the value the user chose in the work order
 * form, the value the backend validates on PATCH /work-orders/:id/roast-type, and
 * the value the list filter matches against. The roast profile snapshot
 * (work_order_recipes.roast_type) is only a fallback for work orders that never
 * got one of their own — linking an Artisan roast must not silently rewrite the
 * roast type the user picked.
 */
export function resolveWorkOrderRoastType(
  workOrderRoastType: string | null | undefined,
  profileRoastType: string | null | undefined,
): RoastType | '' {
  return normalizeRoastType(workOrderRoastType) || normalizeRoastType(profileRoastType);
}
